import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { CalendarCheck } from "lucide-react";
import { reqTitle } from "@/components/family/FamilyForms";

const ST = { pending: "border-gold/40 bg-gold-light/50", approved: "border-sage/40 bg-sage-light/50", declined: "border-border bg-secondary", done: "border-border bg-secondary" };

function Row({ r, carers, onDone }) {
  const [carer, setCarer] = useState("");
  const review = async (status) => {
    const note = status === "declined" ? window.prompt("Message to the family (optional):", "") : "";
    if (note === null) return;
    try { await api.put(`/family-requests/${r.id}/review`, { status, note }); toast.success(`Request ${status}`); onDone(); } catch (e) { toast.error(apiError(e)); }
  };
  const schedule = async (override = false) => {
    if (!carer) { toast.error("Choose a befriender"); return; }
    try {
      const { data } = await api.post(`/family-requests/${r.id}/schedule`, { carer_id: carer, override });
      if (data.scheduled.length) toast.success(`Booked ${data.scheduled.length} visit(s) on the roster`);
      if (data.errors.length) {
        const overHours = data.errors.some((e) => String(e.detail).includes("Confirm to schedule anyway"));
        toast.error(data.errors.map((e) => `${e.date}: ${e.detail}`).join("\n"));
        if (overHours && window.confirm("Some dates go over the befriender's contracted hours. Book them anyway?")) return schedule(true);
      }
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remaining = (r.all_dates || []).filter((d) => !(r.scheduled_dates || []).includes(d));
  return (
    <div data-testid={`admin-family-req-${r.id}`} className={`rounded-lg border p-3 ${ST[r.status]}`}>
      <div className="flex items-center justify-between gap-2"><p className="text-sm font-medium text-ink">{reqTitle(r)}</p><span className="text-[11px] font-semibold capitalize text-ink-soft">{r.status}</span></div>
      {r.kind === "appointment" && <p className="mt-1 text-xs text-ink-soft">{r.address}{r.needs_befriender ? " · befriender requested" : ""}</p>}
      {r.kind === "extra_days" && <p className="mt-1 text-xs text-ink-soft">{(r.all_dates || []).join(", ")}</p>}
      {r.notes && <p className="mt-1 text-sm text-ink-soft">{r.notes}</p>}
      <p className="mt-1 text-xs text-ink-muted">From {r.requested_by}{(r.scheduled_dates || []).length ? ` · booked ${r.scheduled_dates.length}` : ""}</p>
      {r.status === "pending" && (
        <div className="mt-2 flex gap-2">
          <button onClick={() => review(r.kind === "correction" ? "done" : "approved")} data-testid={`approve-family-req-${r.id}`} className="rounded-lg bg-sage px-3 py-1.5 text-xs font-semibold text-white">{r.kind === "correction" ? "Mark done" : "Approve"}</button>
          <button onClick={() => review("declined")} data-testid={`decline-family-req-${r.id}`} className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-coral">Decline</button>
        </div>
      )}
      {r.status === "approved" && r.kind !== "correction" && remaining.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          <select value={carer} onChange={(e) => setCarer(e.target.value)} data-testid={`schedule-carer-${r.id}`} className="rounded-lg border border-border bg-white px-2 py-1.5 text-xs">
            <option value="">Choose befriender…</option>{carers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button onClick={() => schedule(false)} data-testid={`schedule-family-req-${r.id}`} className="inline-flex items-center gap-1 rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white"><CalendarCheck size={13} /> Add {remaining.length} to roster</button>
        </div>
      )}
    </div>
  );
}

export function FamilyRequestsAdmin({ clientId, carers, canManage }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get(`/family-requests?client_id=${clientId}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="client-family-requests">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted"><CalendarCheck size={13} /> Appointments, extra days &amp; corrections from family</p>
      {rows.length === 0 ? <p className="mt-1.5 text-sm text-ink-muted">Nothing from the family yet.</p> : (
        <div className="mt-2 space-y-2">{rows.map((r) => canManage ? <Row key={r.id} r={r} carers={carers} onDone={load} /> : <div key={r.id} className="rounded-lg border border-border p-3 text-sm">{reqTitle(r)} · {r.status}</div>)}</div>
      )}
    </div>
  );
}
